import { useCallback } from "react";
import { jobService } from "@/modules/services/jobService";
import { useSavedJobStore } from "@/modules/store/jobStore";
import { Job } from "@/modules/types/job/job";
import { StatusCode } from "@/modules/types/statusCode";
import { AppError } from "@/lib/utils/errors";

export const useSaveJob = () => {
  const {
    status,
    savedJobs,
    total,
    nextId,
    setStatus,
    setSavedJobs,
    addSavedJob,
    removeSavedJob,
    clearSavedJobs,
    isJobSaved,
  } = useSavedJobStore();

  const saveJob = useCallback(
    async (jobId: string, employeeId: string) => {
      try {
        setStatus("loading");
        const response = await jobService.saveJob(jobId, employeeId);

        if (response.code === StatusCode.OK) {
          // Find the job in the current saved jobs or add it if not present
          const existingJob = savedJobs.find((job) => job.id === jobId);
          if (!existingJob) {
            // You might want to fetch the job details here or pass the job object
            // For now, we'll create a minimal job object
            const jobToAdd: Job = {
              id: jobId,
              isSaved: true,
            } as Job;
            addSavedJob(jobToAdd);
          }
          setStatus("success");
          return { success: true };
        } else {
          setStatus("error");
          return { success: false, error: response.message };
        }
      } catch (error) {
        const appErr = new AppError(error);
        appErr.setAction("useSaveJob.saveJob");
        appErr.setApiAction("jobService.saveJob");
        appErr.logError("src/modules/hooks/job/useSaveJob.ts[saveJob]");
        return {
          success: false,
          error: appErr.message || "Failed to save job",
        };
      }
    },
    [setStatus, savedJobs, addSavedJob]
  );

  const unSaveJob = useCallback(
    async (jobId: string, employeeId: string) => {
      try {
        setStatus("loading");
        const response = await jobService.unSaveJob(jobId, employeeId);

        if (response.code === StatusCode.OK) {
          removeSavedJob(jobId);
          setStatus("success");
          return { success: true };
        } else {
          setStatus("error");
          return { success: false, error: response.message };
        }
      } catch (error) {
        const appErr = new AppError(error);
        appErr.setAction("useSaveJob.unSaveJob");
        appErr.setApiAction("jobService.unSaveJob");
        appErr.logError("src/modules/hooks/job/useSaveJob.ts[unSaveJob]");
        return {
          success: false,
          error: appErr.message || "Failed to unsave job",
        };
      }
    },
    [setStatus, removeSavedJob]
  );

  const fetchSavedJobs = useCallback(
    async (employeeId: string) => {
      try {
        setStatus("loading");
        const response = await jobService.getSavedJobs(employeeId);

        if (response.code === StatusCode.OK && response.jobs) {
          setSavedJobs(response.jobs, response.total, response.nextId);
          setStatus("success");
          return { success: true, jobs: response.jobs };
        } else {
          setStatus("error");
          return { success: false, error: response.message };
        }
      } catch (error) {
        const appErr = new AppError(error);
        appErr.setAction("useSaveJob.fetchSavedJobs");
        appErr.setApiAction("jobService.getSavedJobs");
        appErr.logError("src/modules/hooks/job/useSaveJob.ts[fetchSavedJobs]");
        return {
          success: false,
          error: appErr.message || "Failed to fetch saved jobs",
        };
      }
    },
    [setStatus, setSavedJobs]
  );

  const toggleSaveJob = useCallback(
    async (jobId: string, employeeId: string) => {
      const isCurrentlySaved = isJobSaved(jobId);

      if (isCurrentlySaved) {
        return await unSaveJob(jobId, employeeId);
      } else {
        return await saveJob(jobId, employeeId);
      }
    },
    [isJobSaved, unSaveJob, saveJob]
  );

  return {
    // State
    status,
    savedJobs,
    total,
    nextId,
    isJobSaved,

    // Actions
    saveJob,
    unSaveJob,
    fetchSavedJobs,
    toggleSaveJob,
    clearSavedJobs,
  };
};
